import { useMemo } from 'react'
import {
  BarChart3,
  FileSearch,
  FileText,
  Filter,
  Inbox,
  Landmark,
  PenTool,
  Users,
} from 'lucide-react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import {
  TRACKER_STAGES,
  buildTrackerStats,
  formatTrackerMoney,
  type TrackerStageId,
  type TrackerStat,
  type TrackerStatus,
} from './stageTracker'
import { formatMoneyByCurrency, sumMoneyByCurrency } from './currency'
import { requestedCommitment } from './helpers'

/** Arrow depth in pixels. Overlap must match this so each tip seats in the next notch. */
const ARROW_PX = 28

const SELECTED_FILL = '#8A6A12'
const UNSELECTED_FILL = '#0C0C0C'

type TileKind = 'first' | 'chevron' | 'last'

function tileKind(index: number, total: number): TileKind {
  if (index === 0) return 'first'
  if (index === total - 1) return 'last'
  return 'chevron'
}

function chevronClipPath(kind: TileKind): string {
  const a = `${ARROW_PX}px`
  switch (kind) {
    case 'first':
      return `polygon(0 0, calc(100% - ${a}) 0, 100% 50%, calc(100% - ${a}) 100%, 0 100%)`
    case 'chevron':
      return `polygon(0 0, calc(100% - ${a}) 0, 100% 50%, calc(100% - ${a}) 100%, 0 100%, ${a} 50%)`
    case 'last':
      return `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${a} 50%)`
    default: {
      const _exhaustive: never = kind
      return _exhaustive
    }
  }
}

const STAGE_ICONS: Record<Exclude<TrackerStageId, 'Closing'>, typeof Inbox> = {
  Intake: Inbox,
  Triage: Filter,
  'Pre-Screen': FileSearch,
  Quote: FileText,
  Underwriting: BarChart3,
  'Term Sheet': PenTool,
  Committee: Users,
  Funded: Landmark,
}

function HandshakeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M8.5 12.5 6 10l2.2-2.2a1.5 1.5 0 0 1 2.1 0L12 9.5" />
      <path d="M15.5 12.5 18 10l-2.2-2.2a1.5 1.5 0 0 0 -2.1 0L12 9.5" />
      <path d="M7.5 13.5 10 16a2.2 2.2 0 0 0 3.1 0l3.4-3.4" />
      <path d="M9.2 11.2 11 13" />
      <path d="M13 10.8 14.8 12.6" />
    </svg>
  )
}

function statusStroke(status: TrackerStatus, selected: boolean): string {
  if (selected) return '#FFD86B'
  switch (status) {
    case 'completed':
      return '#34d399'
    case 'current':
      return '#FFD86B'
    case 'upcoming':
      return 'rgba(255, 255, 255, 0.62)'
    default: {
      const _exhaustive: never = status
      return _exhaustive
    }
  }
}

/** 1px hairline around clip-path chevrons. Cardinal offsets only — diagonals stack into a chunky frame. */
const OUTLINE_PX = 1

function chevronOutline(color: string, rim: number): string {
  const d = rim
  return [
    `drop-shadow(${d}px 0 0 ${color})`,
    `drop-shadow(${-d}px 0 0 ${color})`,
    `drop-shadow(0 ${d}px 0 ${color})`,
    `drop-shadow(0 ${-d}px 0 ${color})`,
  ].join(' ')
}

function ChevronTile({
  stat,
  index,
  total,
  selected,
  onSelect,
}: {
  stat: TrackerStat
  index: number
  total: number
  selected: boolean
  onSelect: (stage: string) => void
}) {
  const Icon = stat.id === 'Closing' ? HandshakeIcon : STAGE_ICONS[stat.id]
  const clickable = stat.loanStage != null
  const muted = !selected && stat.status === 'upcoming' && stat.count === 0
  const kind = tileKind(index, total)
  const stroke = statusStroke(stat.status, selected)
  const rim = OUTLINE_PX

  return (
    <span
      className="relative flex h-[6.5rem] min-w-0 flex-1"
      style={{
        marginLeft: index === 0 ? 0 : -ARROW_PX,
        zIndex: selected ? total + 2 : index + 1,
        filter: chevronOutline(stroke, rim),
      }}
    >
      <button
        type="button"
        disabled={!clickable}
        onClick={() => {
          if (!stat.loanStage) return
          onSelect(selected ? 'All' : stat.loanStage)
        }}
        aria-pressed={selected}
        aria-label={
          stat.loanStage
            ? `Stage ${index + 1} of ${total}: ${stat.label}. ${stat.count} deals, ${formatTrackerMoney(stat.volume)}`
            : `${stat.label} — deals enter the book at Triage`
        }
        title={
          stat.loanStage
            ? `${stat.loanStage}${stat.sublabel ? ` — ${stat.sublabel}` : ''}. ${stat.count} deals, ${formatTrackerMoney(stat.volume)}`
            : 'Intake happens before a deal is on the book'
        }
        className={clsx(
          'group flex h-full w-full min-w-0 flex-col items-center justify-center overflow-hidden py-1.5 text-center',
          clickable ? 'cursor-pointer' : 'cursor-default',
        )}
        style={{
          clipPath: chevronClipPath(kind),
          backgroundColor: selected ? SELECTED_FILL : UNSELECTED_FILL,
          paddingLeft: kind === 'first' ? 14 : ARROW_PX + 8,
          paddingRight: kind === 'last' ? 14 : ARROW_PX + 8,
        }}
      >
        <Icon
          className={clsx(
            'h-4 w-4',
            selected ? 'text-[#1A1408]' : muted ? 'text-stone-600' : 'text-[#FFD86B]',
          )}
        />
        <span
          className={clsx(
            'mt-1 max-w-full truncate text-[8px] font-medium uppercase tracking-[0.12em]',
            selected ? 'text-[#1A1408]' : muted ? 'text-stone-600' : 'text-stone-200',
          )}
        >
          {stat.label}
        </span>
        {stat.sublabel ? (
          <span
            className={clsx(
              'max-w-full truncate text-[7px] uppercase tracking-[0.1em]',
              selected ? 'text-[#1A1408]/70' : 'text-stone-500',
            )}
          >
            {stat.sublabel}
          </span>
        ) : null}
        <span
          className={clsx(
            'mt-0.5 text-[22px] font-semibold leading-none',
            selected ? 'text-[#0B0B0B]' : muted ? 'text-stone-600' : 'text-white',
          )}
        >
          {stat.count}
        </span>
        <span
          className={clsx(
            'mt-0.5 text-[10px] leading-none',
            selected ? 'text-[#1A1408]/80' : muted ? 'text-stone-700' : 'text-stone-500',
          )}
        >
          {formatTrackerMoney(stat.volume)}
        </span>
      </button>
    </span>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-stone-500">
      <span className={clsx('h-1.5 w-1.5 rounded-full', color)} />
      {label}
    </span>
  )
}

export function StageRail({
  loans,
  activeStage,
  onSelectStage,
  mode = 'live',
}: {
  loans: Loan[]
  activeStage: string
  onSelectStage: (stage: string) => void
  /** 'dead' counts dead deals under the stage they died at. Progress colouring
   *  (completed / in progress / upcoming) says nothing about a deal that is no
   *  longer moving, so it is dropped rather than left to imply a funnel. */
  mode?: 'live' | 'dead'
}) {
  const built = useMemo(() => buildTrackerStats(loans, activeStage), [loans, activeStage])
  const { total, totalVolume } = built
  const byCurrency = useMemo(() => sumMoneyByCurrency(loans, requestedCommitment), [loans])
  const volumeText =
    byCurrency.totals.length === 0 ? formatTrackerMoney(totalVolume) : formatMoneyByCurrency(byCurrency, formatTrackerMoney)
  const dead = mode === 'dead'
  const stats = dead ? built.stats.map((stat) => ({ ...stat, status: 'upcoming' as const })) : built.stats

  return (
    <section aria-label="Pipeline stages" className="flex-shrink-0 border-b border-amber-300/10 px-4 py-3">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-[#FFD86B]">
            {dead ? 'Dead by Stage at Death' : 'Pipeline Stages'}
          </span>
          <span className="hidden h-3 w-px bg-white/15 sm:block" />
          <span className="text-[11px] text-stone-400">
            {total} {dead ? 'Dead Deals' : 'Total Deals'} • {volumeText} Requested (total commitment)
          </span>
        </div>
        {dead ? null : (
          <div className="flex items-center gap-3">
            <LegendDot color="bg-emerald-500" label="Completed" />
            <LegendDot color="bg-[#E8A317]" label="In Progress" />
            <LegendDot color="bg-stone-500" label="Upcoming" />
          </div>
        )}
      </div>

      <div className="min-w-0 overflow-x-auto py-2">
        <div className="isolate flex min-w-[720px] items-stretch px-1">
          {stats.map((stat, index) => (
            <ChevronTile
              key={stat.id}
              stat={stat}
              index={index}
              total={TRACKER_STAGES.length}
              selected={stat.loanStage != null && activeStage === stat.loanStage}
              onSelect={onSelectStage}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
